import './ConversationWidget.css';
import { useConversation } from '@11labs/react';
import { useCallback, useState, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

const API_URL = process.env.REACT_APP_API_URL || "https://nayademo-production.up.railway.app";

type Role = 'ai' | 'user';

interface ConversationMessage {
  message: string;
  source: Role;
}

interface Report {
  structured_brief: {
    updates: string;
    challenges: string;
    wellbeing: string;
    feedback: string;
  };
  briefing_script: string;
  conversation_id: string;
  audio_url?: string;
  conversation_audio_url?: string;
  conversation_recorded_at?: string;
}

interface ConversationData {
  feedback_for_leadership: string;
  challenges: string;
  work_updates: string;
  well_being: string;
}

interface ReportFeedback {
  isPositive: boolean;
  reportId: string;
  timestamp: string;
  promptVersion?: string;
  reportType?: string;
}

interface ConversationFeedback {
  rating: boolean;
  conversationId: string;
  timestamp: string;
  promptVersion?: string;
}

interface FeedbackSection {
  title: string;
  question: string;
  type: 'conversation' | 'report';
}

const FeedbackComponent: React.FC<{
  section: FeedbackSection;
  onSubmit: (isPositive: boolean, type: string) => void;
  isVisible: boolean;
}> = ({ section, onSubmit, isVisible }) => {
  if (!isVisible) return null;

  return (
    <div className="feedback-section">
      <h4>{section.title}</h4>
      <p>{section.question}</p>
      <div className="feedback-buttons">
        <button
          onClick={() => onSubmit(true, section.type)}
          className="feedback-button positive"
          aria-label="Thumbs up"
        >
          👍
        </button>
        <button
          onClick={() => onSubmit(false, section.type)}
          className="feedback-button negative"
          aria-label="Thumbs down"
        >
          👎
        </button>
      </div>
    </div>
  );
};

export function ConversationWidget() {
  const [error, setError] = useState<string | null>(null);
  const [conversationLog, setConversationLog] = useState<ConversationMessage[]>([]);
  const [conversationId, setConversationId] = useState<string>('');
  const [report, setReport] = useState<Report | null>(null);
  const [conversationData, setConversationData] = useState<ConversationData | null>(null);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [feedbackButtonVisible, setFeedbackButtonVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const apiKey = process.env.REACT_APP_ELEVEN_LABS_API_KEY;
  const [isConversationActive, setIsConversationActive] = useState(false);
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [reportText, setReportText] = useState<string | null>(null);
  const [reportFeedbackGiven, setReportFeedbackGiven] = useState<boolean>(false);
  const [reportFeedbackVisible, setReportFeedbackVisible] = useState<boolean>(false);
  const [conversationFeedbackGiven, setConversationFeedbackGiven] = useState<boolean>(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const [isPlayingConversation, setIsPlayingConversation] = useState<boolean>(false);
  const [conversationAudioUrl, setConversationAudioUrl] = useState<string | null>(null);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const processConversationData = (messages: ConversationMessage[]) => {
    const data: ConversationData = {
      feedback_for_leadership: '',
      challenges: '',
      work_updates: '',
      well_being: ''
    };

    messages.forEach((msg, index) => {
      if (msg.source === 'user') {
        // Get the previous AI message
        let prevIndex = index - 1;
        while (prevIndex >= 0 && messages[prevIndex].source !== 'ai') {
          prevIndex--;
        }
        const lastAiMessage = messages[prevIndex]?.message.toLowerCase() || '';
        
        if (lastAiMessage.includes('feedback') || lastAiMessage.includes('suggest')) {
          data.feedback_for_leadership = msg.message;
        } else if (lastAiMessage.includes('work') || lastAiMessage.includes('progress')) {
          data.work_updates = msg.message;
        } else if (lastAiMessage.includes('challenges') || lastAiMessage.includes('issues')) {
          data.challenges = msg.message;
        } else if (lastAiMessage.includes('feeling') || lastAiMessage.includes('role')) {
          data.well_being = msg.message;
        }
      }
    });

    console.log('Processed conversation data:', data);
    setConversationData(data);
  };

  const conversation = useConversation({
    onConnect: () => console.log('Connected'),
    onDisconnect: () => console.log('Disconnected'),
    onMessage: (props: { message: string; source: Role; metadata?: any }) => {
      console.log('Received message:', props);
      
      const newMessage: ConversationMessage = {
        message: props.message,
        source: props.source
      };
      
      setConversationLog(prev => {
        const updatedLog = [...prev, newMessage];
        processConversationData(updatedLog);
        return updatedLog;
      });

      // If there's a transcript, update with the full history
      if (props.metadata?.transcript) {
        const transcriptMessages = props.metadata.transcript.map((entry: any) => ({
          message: entry.message,
          source: entry.role === 'agent' ? 'ai' : 'user'
        }));
        setConversationLog(transcriptMessages);
      }

      // Check for analysis data
      if (props.metadata?.analysis?.data_collection_results) {
        console.log('Received data collection results:', props.metadata.analysis.data_collection_results);
        const results = props.metadata.analysis.data_collection_results;
        // Only update if we get valid data
        const newData: ConversationData = {
          feedback_for_leadership: results.feedback_for_leadership?.value || conversationData?.feedback_for_leadership || '',
          challenges: results.challenges?.value || conversationData?.challenges || '',
          work_updates: results.work_updates?.value || conversationData?.work_updates || '',
          well_being: results.well_being?.value || conversationData?.well_being || ''
        };
        setConversationData(newData);
      }
    },
    onError: (err: Error | string) => {
      console.error('Error:', err);
      setError(typeof err === 'string' ? err : err.message);
    },
  });

  const startConversation = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const agentId = process.env.REACT_APP_ELEVEN_LABS_AGENT_ID;
      if (!agentId) {
        throw new Error('Agent ID is not configured');
      }
      const convId = await conversation.startSession({
        agentId: agentId,
      });
      setConversationId(convId);
    } catch (error: any) {
      console.error('Failed to start conversation:', error);
      setError('Failed to start conversation. Please check your microphone permissions.');
    }
  }, [conversation, setError]);

  const generateReport = useCallback(async () => {
    try {
        setError('Processing conversation...');
        setIsLoading(true);

        const transcript = conversationLog
            .map(msg => `${msg.source}: ${msg.message}`)
            .join('\n');

        // Log the actual URL being used
        console.log('Using API URL:', API_URL);
        console.log('Sending request to:', `${API_URL}/generate-report`);

        const response = await fetch(`${API_URL}/generate-report`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                conversationId: conversationId,
                transcript: transcript
            }),
            credentials: 'include',
            mode: 'cors'
        });

        // Log the response details
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers));

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`Server error: ${errorText}`);
        }

        const data = await response.json();
        console.log('Report data:', data);
        
        setReport(data);
        setReportFeedbackVisible(true);
        setError(null);
    } catch (error) {
        console.error('Full error:', error);
        setError(error instanceof Error ? error.message : 'Failed to generate report');
    } finally {
        setIsLoading(false);
    }
}, [conversationLog, conversationId]);

  const stopConversation = useCallback(async () => {
    try {
      await conversation.endSession();
      setError('Ending session...');
      
      if (!conversationId || !apiKey) {
        throw new Error('Missing conversation ID or API key');
      }

      // Increase delay to 10 seconds to allow for processing
      await new Promise(resolve => setTimeout(resolve, 10000));

      // Fetch conversation details after ending session
      const elevenLabsResponse = await fetch(
        `https://api.elevenlabs.io/v1/convai/conversations/${conversationId}`,
        {
          headers: {
            'xi-api-key': apiKey,
            'Accept': 'application/json'
          }
        }
      );

      if (!elevenLabsResponse.ok) {
        if (elevenLabsResponse.status === 404) {
          // If conversation not found, proceed with report generation anyway
          console.warn('Conversation details not found, proceeding with local data');
          setError(null);
          await generateReport();
          return;
        }
        throw new Error(`Failed to fetch conversation details: ${elevenLabsResponse.status}`);
      }

      const conversationDetails = await elevenLabsResponse.json();
      console.log('Full Conversation Details:', JSON.stringify(conversationDetails, null, 2));
      
      // Log specific parts we're interested in
      console.log('Analysis:', conversationDetails.analysis);
      console.log('Data Collection Results:', conversationDetails.analysis?.data_collection_results);
      
      setError(null);

      // Update conversation data from analysis
      if (conversationDetails.analysis?.data_collection_results) {
        const results = conversationDetails.analysis.data_collection_results;
        console.log('Processing results:', results);
        
        setConversationData({
          feedback_for_leadership: results.feedback_for_leadership?.value || '',
          challenges: results.challenges?.value || '',
          work_updates: results.work_updates?.value || '',
          well_being: results.well_being?.value || ''
        });
      } else {
        console.warn('No data collection results found in:', conversationDetails);
      }

      if (conversationDetails.transcript) {
        const transcriptMessages = conversationDetails.transcript.map((entry: any) => ({
          message: entry.message,
          source: entry.role === 'agent' ? 'ai' : 'user'
        }));
        setConversationLog(transcriptMessages);
      }

      setShowFeedback(true);
      setFeedbackButtonVisible(true);

      // Automatically generate report
      await generateReport();
    } catch (error) {
      console.error('Error stopping conversation:', error);
      // Even if fetching details fails, try to generate report
      await generateReport();
      setError(null);
    } finally {
      // Always show feedback form regardless of success/failure
      setShowFeedback(true);
      setFeedbackButtonVisible(true);
    }
  }, [conversation, conversationId, apiKey, generateReport]);

  const downloadPDF = async () => {
    try {
        const API_URL = process.env.REACT_APP_API_URL || "https://nayademo-production.up.railway.app";
        const response = await fetch(`${API_URL}/download-report`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                // The UI report sections
                uiReport: {
                    updates: report?.structured_brief.updates,
                    challenges: report?.structured_brief.challenges,
                    wellbeing: report?.structured_brief.wellbeing,
                    feedback: report?.structured_brief.feedback,
                },
                // The conversational briefing script
                briefingText: report?.briefing_script
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `report-${new Date().toISOString().split("T")[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        console.error("Error downloading PDF:", error);
        setError("Failed to download PDF");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunks.current.push(e.data);
        }
      };
      
      recorder.start(1000); // Collect data every second
      setMediaRecorder(recorder);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setError('Failed to start recording. Please check your microphone permissions.');
    }
  };

  const stopRecording = async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorder) {
        resolve(null);
        return;
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        audioChunks.current = [];
        resolve(audioBlob);
      };

      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setMediaRecorder(null);
    });
  };

  const toggleConversation = async () => {
    try {
      if (!isConversationActive) {
        setIsConversationActive(true);
        setError('Initializing conversation...');
        await startConversation();
        await startRecording(); // Start recording when conversation starts
        setError(null);
      } else {
        setIsConversationActive(false);
        setError('Ending conversation...');
        
        // 1. Stop recording and get the audio blob
        const audioBlob = await stopRecording();
        
        // 2. Upload the audio file first and get the URL
        let audioUrl = null;
        if (audioBlob) {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'conversation.webm');
          formData.append('conversation_id', conversationId);
          
          try {
            const uploadResponse = await fetch(`${API_URL}/upload-audio`, {
              method: 'POST',
              body: formData,
            });
            
            if (!uploadResponse.ok) {
              throw new Error('Failed to upload audio recording');
            }
            
            const uploadData = await uploadResponse.json();
            audioUrl = uploadData.audio_url;
            setConversationAudioUrl(audioUrl);
          } catch (error) {
            console.error('Failed to upload audio recording:', error);
          }
        }
        
        // 3. End the conversation and generate report
        await conversation.endSession();
        
        if (!conversationId || !apiKey) {
          throw new Error('Missing conversation ID or API key');
        }

        // Increase delay to 10 seconds to allow for processing
        await new Promise(resolve => setTimeout(resolve, 10000));

        // 4. Fetch conversation details after ending session
        const elevenLabsResponse = await fetch(
          `https://api.elevenlabs.io/v1/convai/conversations/${conversationId}`,
          {
            headers: {
              'xi-api-key': apiKey,
              'Accept': 'application/json'
            }
          }
        );

        if (!elevenLabsResponse.ok) {
          if (elevenLabsResponse.status === 404) {
            console.warn('Conversation details not found, proceeding with local data');
            setError(null);
            // Generate report with audio URL
            const transcript = conversationLog
              .map(msg => `${msg.source}: ${msg.message}`)
              .join('\n');
              
            const reportResponse = await fetch(`${API_URL}/generate-report`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                conversationId: conversationId,
                transcript: transcript,
                conversation_audio_url: audioUrl
              }),
            });
            
            if (!reportResponse.ok) {
              throw new Error('Failed to generate report');
            }
            
            const reportData = await reportResponse.json();
            setReport(reportData);
            setReportFeedbackVisible(true);
            return;
          }
          throw new Error(`Failed to fetch conversation details: ${elevenLabsResponse.status}`);
        }

        const conversationDetails = await elevenLabsResponse.json();
        console.log('Full Conversation Details:', JSON.stringify(conversationDetails, null, 2));
        
        if (conversationDetails.analysis?.data_collection_results) {
          const results = conversationDetails.analysis.data_collection_results;
          setConversationData({
            feedback_for_leadership: results.feedback_for_leadership?.value || '',
            challenges: results.challenges?.value || '',
            work_updates: results.work_updates?.value || '',
            well_being: results.well_being?.value || ''
          });
        }

        if (conversationDetails.transcript) {
          const transcriptMessages = conversationDetails.transcript.map((entry: any) => ({
            message: entry.message,
            source: entry.role === 'agent' ? 'ai' : 'user'
          }));
          setConversationLog(transcriptMessages);
        }

        setShowFeedback(true);
        setFeedbackButtonVisible(true);

        // Generate report with audio URL
        const transcript = conversationLog
          .map(msg => `${msg.source}: ${msg.message}`)
          .join('\n');
          
        const reportResponse = await fetch(`${API_URL}/generate-report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            conversationId: conversationId,
            transcript: transcript,
            conversation_audio_url: audioUrl
          }),
        });
        
        if (!reportResponse.ok) {
          throw new Error('Failed to generate report');
        }
        
        const reportData = await reportResponse.json();
        setReport(reportData);
        setReportFeedbackVisible(true);
      }
    } catch (error) {
      console.error('Error toggling conversation:', error);
      setError(error instanceof Error ? error.message : 'Failed to toggle conversation');
      setIsConversationActive(false);
    }
  };

  const toggleFeedback = () => {
    setIsFeedbackVisible(!isFeedbackVisible);
  };

  const verifyConversationSaved = async () => {
    try {
        const response = await fetch(`${API_URL}/conversations/${conversationId}`);
        if (!response.ok) {
            throw new Error('Conversation not saved correctly');
        }
        const data = await response.json();
        console.log('Saved conversation data:', data);
        return data.status === 'success';
    } catch (error) {
        console.error('Error verifying conversation:', error);
        return false;
    }
  };

  const playReport = async () => {
    try {
        if (!report) {
            throw new Error('No report available to play');
        }

        setIsProcessing(true);
        const reportText = report.briefing_script;
        
        const response = await fetch(`${API_URL}/generate-audio`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                text: reportText,
                report_id: report.conversation_id 
            }),
            credentials: 'include',
            mode: 'cors'
        });

        if (!response.ok) {
            const errorData = await response.json();
            if (errorData.detail?.status === 'quota_exceeded') {
                throw new Error('Audio generation quota exceeded. Please try again later or contact support.');
            }
            throw new Error(`Audio generation failed: ${errorData.detail?.message || 'Unknown error'}`);
        }

        // Handle the new response format
        const data = await response.json();
        if (data.status === 'success' && data.audio_url) {
            const audio = new Audio(data.audio_url);
            
            setIsProcessing(false);
            setIsPlaying(true);
            setAudioUrl(data.audio_url); // Store the S3 URL
            
            audio.onended = () => {
                setIsPlaying(false);
            };

            setReportText(reportText);
            await audio.play();
        } else {
            throw new Error('No audio URL received');
        }
    } catch (error) {
        setIsProcessing(false);
        console.error('Error playing report:', error);
        setError(error instanceof Error ? error.message : 'Failed to play report');
        setIsPlaying(false);
    }
};

  const submitReportFeedback = async (isPositive: boolean, reportType: string) => {
    try {
        const feedback: ReportFeedback = {
            isPositive,
            reportId: conversationId,
            timestamp: new Date().toISOString(),
            promptVersion: "v1",
            reportType
        };

        const response = await fetch(`${API_URL}/report-feedback`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(feedback)
        });

        if (!response.ok) {
            throw new Error('Failed to submit feedback');
        }

        setReportFeedbackGiven(true);
        setReportFeedbackVisible(false);
        
        // Show a thank you message briefly
        setError('Thank you for your feedback!');
        setTimeout(() => setError(null), 3000);
    } catch (error) {
        console.error('Error submitting feedback:', error);
        setError('Failed to submit feedback');
    }
  };

  const submitConversationFeedback = async (isPositive: boolean) => {
    try {
      const feedback: ConversationFeedback = {
        rating: isPositive,
        conversationId: conversationId,
        timestamp: new Date().toISOString(),
        promptVersion: "v1"
      };

      const response = await fetch(`${API_URL}/conversation-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedback)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setConversationFeedbackGiven(true);
      setError('Thank you for your feedback!');
      setTimeout(() => setError(null), 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback');
    }
  };

  const playConversation = async () => {
    try {
      if (!report?.conversation_audio_url) {
        throw new Error('No conversation recording available');
      }

      setIsPlayingConversation(true);
      const audio = new Audio(report.conversation_audio_url);
      
      audio.onended = () => {
        setIsPlayingConversation(false);
      };

      await audio.play();
    } catch (error) {
      console.error('Error playing conversation:', error);
      setError(error instanceof Error ? error.message : 'Failed to play conversation');
      setIsPlayingConversation(false);
    }
  };

  const handleSendReport = () => {
    setOpenEmailDialog(true);
  };

  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setEmail('');
  };

  const handleEmailSubmit = async () => {
    try {
      setSendingEmail(true);
      const response = await fetch(`${API_URL}/send-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          conversationId: conversationId,
          report: {
            structured_brief: report?.structured_brief,
            briefing_script: report?.briefing_script
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send report');
      }

      setError('Report sent successfully!');
      setTimeout(() => setError(null), 3000);
      handleCloseEmailDialog();
    } catch (error) {
      console.error('Error sending report:', error);
      setError('Failed to send report. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="interaction-area">
      <div className="controls">
        <div className="button-container">
          <button
            onClick={toggleConversation}
            className={`primary-button ${isConversationActive ? "active" : ""}`}
          >
            {isConversationActive ? "Stop Conversation" : "Start Conversation"}
          </button>
          {feedbackButtonVisible && !showFeedback && (
            <button
              onClick={() => setShowFeedback(true)}
              className="secondary-button"
            >
              Provide Feedback
            </button>
          )}
        </div>
      </div>

      <div className="status-area">
        {/* <p className="status-message">Status: {conversation.status}</p>
        <p className="status-message">Bot is {conversation.isSpeaking ? 'speaking' : 'listening'}</p> */}
        {error && (
          <div className="status-message-container">
            <p
              className={
                error.includes("Ending")
                  ? "ending-message"
                  : error.includes("Processing") ||
                    error.includes("Generating") ||
                    error.includes("Still")
                  ? "processing-message"
                  : "warning-message"
              }
            >
              {error}
            </p>
            {isLoading && (
              <CircularProgress size={20} className="loading-spinner" />
            )}
          </div>
        )}
      </div>

      {report && (
        <div className="report-area">
          <div className="report-header">
            <h2>Leadership Briefing</h2>
            <div className="report-actions">
              <button
                onClick={playReport}
                className="secondary-button"
                disabled={isPlaying || isProcessing}
              >
                {isProcessing
                  ? "Processing..."
                  : isPlaying
                  ? "Playing..."
                  : "Play Briefing"}
              </button>
              {report.conversation_audio_url && (
                <button
                  onClick={playConversation}
                  className="secondary-button"
                  disabled={isPlayingConversation}
                >
                  {isPlayingConversation ? "Playing..." : "Play Conversation"}
                </button>
              )}
              <button onClick={handleSendReport} className="secondary-button">
                Send PDF Report
              </button>
            </div>
          </div>

          {/* Structured Brief */}
          <div className="structured-brief">
            {report.structured_brief.updates && (
              <div className="report-section">
                <h3>Updates & Progress</h3>
                <p>{report.structured_brief.updates}</p>
              </div>
            )}
            {report.structured_brief.challenges && (
              <div className="report-section">
                <h3>Challenges</h3>
                <p>{report.structured_brief.challenges}</p>
              </div>
            )}
            {report.structured_brief.wellbeing && (
              <div className="report-section">
                <h3>Employee Wellbeing</h3>
                <p>{report.structured_brief.wellbeing}</p>
              </div>
            )}
            {report.structured_brief.feedback && (
              <div className="report-section">
                <h3>Feedback for Leadership</h3>
                <p>{report.structured_brief.feedback}</p>
              </div>
            )}
          </div>

          {/* Conversation Quality Feedback */}
          <FeedbackComponent
            section={{
              title: "Conversation Quality",
              question: "How was your conversation experience with Naya? Would you recommend it to others?",
              type: "conversation"
            }}
            onSubmit={(isPositive) => submitConversationFeedback(isPositive)}
            isVisible={!conversationFeedbackGiven && reportFeedbackVisible}
          />

          {/* Report Quality Feedback */}
          <FeedbackComponent
            section={{
              title: "Briefing Quality",
              question: "Was this briefing helpful and accurate? Would you share it with your leadership?",
              type: "report"
            }}
            onSubmit={(isPositive) => submitReportFeedback(isPositive, 'structured')}
            isVisible={!reportFeedbackGiven && reportFeedbackVisible}
          />
        </div>
      )}

      {/* <div className="conversation-log">
        <h3>Conversation Transcript</h3>
        {conversationLog.length === 0 ? (
          <p>No messages yet</p>
        ) : (
          conversationLog.map((msg, index) => (
            <div key={index} className={`message ${msg.source}`}>
              <strong>{msg.source}:</strong> {msg.message}
            </div>
          ))
        )}
      </div> */}

      {showFeedback && (
        <div className="feedback-form">
          <div className="feedback-header">
            <h3>Please provide your feedback</h3>
            <button
              onClick={toggleFeedback}
              className="collapse-button"
              aria-label={
                isFeedbackVisible
                  ? "Collapse feedback form"
                  : "Expand feedback form"
              }
            >
              {isFeedbackVisible ? "▼" : "▲"}
            </button>
          </div>
          {isFeedbackVisible && (
            <iframe
              src="https://docs.google.com/forms/d/e/1FAIpQLSfhujT8nnLnC-_h6WSiOM9ZC5DapAtIJVBnVq-uRVwTT62flA/viewform?embedded=true"
              width={640}
              height={1482}
              frameBorder={0}
              style={{ border: 0 }}
              title="Naya Feedback Survey"
            >
              Loading…
            </iframe>
          )}
        </div>
      )}

      {/* Email Dialog */}
      <Dialog 
        open={openEmailDialog} 
        onClose={handleCloseEmailDialog}
        PaperProps={{
          style: {
            borderRadius: '24px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            background: 'linear-gradient(145.37deg, rgba(255, 255, 255, 0.9) -8.75%, rgba(255, 255, 255, 0.9) 83.95%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle 
          style={{
            fontSize: '28px',
            fontWeight: 700,
            background: 'linear-gradient(90deg, #2B2D42 0%, #1A1A1A 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
            paddingBottom: '16px',
            fontFamily: "'Inter', sans-serif"
          }}
        >
          Get Your Report
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{
              marginTop: '16px',
              '& .MuiOutlinedInput-root': {
                fontFamily: "'Inter', sans-serif",
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '12px',
                '& fieldset': {
                  borderColor: 'rgba(43, 45, 66, 0.2)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: '#2B2D42',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2B2D42',
                  borderWidth: '2px',
                },
              },
              '& .MuiInputLabel-root': {
                fontFamily: "'Inter', sans-serif",
                color: '#2B2D42',
                '&.Mui-focused': {
                  color: '#2B2D42',
                },
              },
              '& .MuiInputBase-input': {
                fontFamily: "'Inter', sans-serif",
                color: '#2B2D42',
              }
            }}
          />
        </DialogContent>
        <DialogActions style={{ padding: '20px 0 0', justifyContent: 'center', gap: '16px' }}>
          <Button 
            onClick={handleCloseEmailDialog}
            style={{
              fontFamily: "'Inter', sans-serif",
              textTransform: 'none',
              padding: '12px 28px',
              borderRadius: '12px',
              color: '#2B2D42',
              backgroundColor: 'rgba(43, 45, 66, 0.1)',
              border: 'none',
              fontWeight: 600,
              fontSize: '16px',
              transition: 'all 0.3s ease',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEmailSubmit} 
            disabled={!email || sendingEmail}
            style={{
              fontFamily: "'Inter', sans-serif",
              textTransform: 'none',
              padding: '12px 28px',
              borderRadius: '12px',
              background: 'linear-gradient(90deg, #2B2D42 0%, #1A1A1A 100%)',
              color: 'white',
              border: 'none',
              opacity: (!email || sendingEmail) ? 0.7 : 1,
              fontWeight: 600,
              fontSize: '16px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            {sendingEmail ? 'Sending...' : 'Send Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
} 